import React from "react";
import { usePermissions } from "../../../hooks/usePermissions";
import UnauthorizedPage from "../../pages/Extra/UnauthorizedPage";
import { MarketplaceLoadingSpinner } from "../../organisms/Dashboard/Marketplace/MarketplaceEmptyState";

export interface PermissionRouteProps {
  permission?: string;
  anyPermission?: string[];
  module?: string;
  children: React.ReactElement;
  redirectPath?: string;
}

/**
 * Route guard component protecting pages from direct URL navigation.
 * Example:
 * <PermissionRoute permission="quotation.read">
 *   <AllQuotationPage />
 * </PermissionRoute>
 */
export const PermissionRoute: React.FC<PermissionRouteProps> = ({
  permission,
  anyPermission,
  module,
  children,
  redirectPath = "/dashboard",
}) => {
  const {
    hasPermission,
    hasAnyPermission,
    hasModule,
    permissionsLoading,
    isStudioOwner,
    isSubUser,
  } = usePermissions();

  // If permissions are actively loading for a sub-user, avoid flashing Unauthorized
  if (permissionsLoading && isSubUser) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <MarketplaceLoadingSpinner padding="80px 20px" />
      </div>
    );
  }

  // Owner always bypasses
  if (isStudioOwner) {
    return children;
  }

  let isAllowed = true;
  if (permission) {
    isAllowed = hasPermission(permission);
  } else if (anyPermission && anyPermission.length > 0) {
    isAllowed = hasAnyPermission(anyPermission);
  } else if (module) {
    isAllowed = hasModule(module);
  }

  if (!isAllowed) {
    return (
      <UnauthorizedPage
        requiredPermission={permission || (anyPermission ? anyPermission.join(", ") : undefined)}
        moduleName={module}
        redirectPath={redirectPath}
      />
    );
  }

  return children;
};

export default PermissionRoute;
